import { LazyLoadImage } from 'react-lazy-load-image-component';
import styled, { css } from 'styled-components';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { FONTS } from '@r1-frontend/ui-react/tokens/fonts';

import { EBlockSize } from '~/src/features/agent/data';

const Local_BorderRadius = '10px';

const getBlockPropertiesBySize = (size: EBlockSize, isOnlyImage?: boolean) => {
    switch (size) {
        case EBlockSize.LARGE:
            return css`
                width: 992px;
                padding-bottom: 0;

                & > div:last-child {
                    width: fit-content;
                    align-self: center;
                }

                img {
                    max-width: 544px;
                }

                @media (max-width: ${wideBreakpoints.sDesktop}) {
                    width: 800px;
                }

                @media (max-width: ${wideBreakpoints.laptop}) {
                    grid-column: 1 / -1;
                    width: 100%;
                    height: 352px;
                    flex-direction: row;
                    padding-bottom: 0;

                    & > div:first-child {
                        max-width: 360px;
                        margin-right: 78px;
                    }

                    & > div:last-child {
                        max-width: 320px;
                        margin-top: -56px;
                    }
                }

                @media (max-width: ${wideBreakpoints.tablet}) {
                    & > div:first-child {
                        max-width: 310px;
                        margin-right: 60px;
                    }

                    & > div:last-child {
                        width: 300px;
                        height: 100%;
                        margin-top: -68px;
                        margin-bottom: 0;
                    }
                }

                @media (max-width: ${wideBreakpoints.mobile}) {
                    flex-direction: column;
                    height: 445px;

                    & > div:first-child {
                        margin-right: 0;
                    }

                    & > div:last-child {
                        max-width: 256px;
                        margin-top: -21px;
                    }
                }
            `;
        case EBlockSize.MIDDLE:
            return css`
                width: 736px;

                @media (max-width: ${wideBreakpoints.sDesktop}) {
                    width: 591px;
                }

                @media (max-width: ${wideBreakpoints.laptop}) {
                    grid-column: 1 / -1;
                    width: 100%;
                    height: 352px;
                    flex-direction: row;

                    & > div:first-child {
                        max-width: 345px;
                        margin-right: 30px;
                    }

                    img {
                        max-height: -webkit-fill-available;
                    }
                }

                @media (max-width: ${wideBreakpoints.mobile}) {
                    flex-direction: column;
                    height: 325px;

                    & > div:first-child {
                        margin-right: 0;
                    }
                }
            `;
        case EBlockSize.SMALL:
        default:
            return css`
                width: 480px;

                @media (max-width: ${wideBreakpoints.sDesktop}) {
                    width: 384px;
                    height: 100%;
                }

                @media (max-width: ${wideBreakpoints.laptop}) {
                    width: 100%;
                    height: 730px;
                }

                @media (max-width: ${wideBreakpoints.mobile}) {
                    height: 555px;

                    & > div:last-child {
                        flex: 1 1 auto;

                        ${isOnlyImage
                            ? css`
                                border-radius: ${BorderRadius.OuterBlockRadius};
                                overflow: hidden;
                                
                                img {
                                    max-height: 555px;
                                }
                            `
                            : css`
                                img {
                                    max-height: 400px;
                                }
                            `
                        }
                    }
                }
            `;
    }
};

export const BlockWrapper = styled.div<{ size: EBlockSize, area: string, isOnlyImage?: boolean }>`
    grid-area: ${p => p.area};
    border-radius: ${BorderRadius.OuterBlockRadius};
    overflow: hidden;

    ${({ isOnlyImage, size }) => isOnlyImage
        ? css`
            @media (max-width: ${wideBreakpoints.sDesktop}) {
                & > div:last-child {
                    margin: unset;
                }

                img {
                    object-fit: cover;
                }
            }
        `
        : css`
            flex-direction: column;
            flex-wrap: nowrap;
            padding: 40px;
            background-color: ${COLORS.BgMain};

            img {
                max-width: ${size === EBlockSize.SMALL ? '345px' : 'unset'};
            }


            @media (max-width: ${wideBreakpoints.laptop}) {
                align-items: flex-start;
                padding: 40px 32px;

                img {
                    max-width: ${size === EBlockSize.SMALL ? '315px' : 'unset'};
                }
            }

            @media (max-width: ${wideBreakpoints.mobile}) {
                align-items: center;
                padding: 24px;

                & > div {
                    max-width: 300px;
                }
            }
        `
    }

    ${({ size, isOnlyImage }) => getBlockPropertiesBySize(size, isOnlyImage)};
`;

export const ContentBlock = styled.div`
    flex-direction: row;

    @media (max-width: ${wideBreakpoints.laptop}) {
        margin-bottom: 30px;
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        margin-bottom: 24px;
    }
`;

export const BlockTitle = styled.h3`
    margin-top: 0;
    margin-bottom: 4px;
    ${FONTS.H2};

    @media (max-width: ${wideBreakpoints.mobile}) {
        ${FONTS.H5};
    }
`;

export const BlockDesc = styled.div`
    ${FONTS.M};
    color: ${COLORS.TextInfo};

    @media (max-width: ${wideBreakpoints.mobile}) {
        ${FONTS.XS};
    }
`;

export const MediaBlock = styled.div`
    position: relative;
    width: 100%;
    margin: auto 0;
    justify-content: center;
`;

export const TypingAnimationBlock = styled.div`
    position: absolute;
    bottom: 47px;
    left: 95px;
    width: 92px;
    height: 44px;
    border-radius: ${BorderRadius.componentRadius};
    overflow: hidden;

    img {
        width: 100%;
        height: auto;
    }

    @media (max-width: ${wideBreakpoints.laptop}) {
        bottom: 29px;
        left: 56px;
        width: 54px;
        height: 26px;
        justify-content: center;
        border-radius: ${Local_BorderRadius};
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        left: 53px;
        bottom: -29px;
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        left: 46px;
        bottom: 15px;
    }
`;

export const BlockImage = styled(LazyLoadImage)`
    width: 100%;
    height: 100%;
    object-position: center;
    object-fit: contain;
`;

export const BlockVideo = styled.video`
    outline: none;
    user-select: none;
    pointer-events: none;
    width: 490px;
`;
